import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingScreen } from '../../contexts/AuthContext';
import AccountStatusPage from './AccountStatusPage';
import Link from '@mui/material/Link';
import { useNavigate } from 'react-router-dom';

interface AuthGuardProps {
  children?: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const { currentUser, signInWithGoogle, loading: authLoading, accountStatus } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const navigate = useNavigate();

  // Redirect to main app after successful sign-in (but not if account is suspended/banned)
  useEffect(() => {
    if (currentUser && !authLoading && !accountStatus) {
      navigate('/', { replace: true });
    }
  }, [currentUser, authLoading, accountStatus, navigate]);

  const handleSignIn = async () => {
    try {
      setSigningIn(true);
      await signInWithGoogle();
    } catch (error: any) {
      // Only log errors that aren't user cancellations
      if (error.code !== 'auth/cancelled-popup-request' && 
          error.code !== 'auth/popup-closed-by-user' &&
          error.code !== 'auth/popup-blocked') {
        console.error('Sign-in error:', error);
      }
    } finally {
      setSigningIn(false);
    }
  };

  if (authLoading) {
    return <LoadingScreen />;
  }

  // Show account status page if user is suspended or banned
  if (accountStatus) {
    return (
      <AccountStatusPage 
        status={accountStatus.type}
        reason={accountStatus.reason}
        endDate={accountStatus.endDate}
      />
    );
  }

  if (!currentUser) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            sx={{
              p: 4,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: 'background.paper'
            }}
          >
            <Typography component="h1" variant="h5" gutterBottom>
              Battleground State
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Sign in to access the latest political news and engage with your community
            </Typography>
            <Box sx={{ width: '100%', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <input
                  type="checkbox"
                  id="consent-checkbox"
                  checked={consentChecked}
                  onChange={e => setConsentChecked(e.target.checked)}
                  style={{ marginRight: 8, marginTop: 2 }}
                />
                <Typography variant="body2" color="text.secondary">
                  By clicking continue, you agree to our{' '}
                  <Link href="/terms" target="_blank" rel="noopener noreferrer" underline="always">
                    Terms of Service
                  </Link>{' '}and{' '}
                  <Link href="/privacy" target="_blank" rel="noopener noreferrer" underline="always">
                    Privacy Policy
                  </Link>.
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={signingIn ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
              onClick={handleSignIn}
              disabled={signingIn || !consentChecked}
              fullWidth
              sx={{ mt: 2 }}
            >
              {signingIn ? 'Signing in...' : 'Sign in with Google'}
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return <>{children}</>;
} 